import { useState, useEffect, useCallback, useMemo } from "react";
import { useApi } from "./useApi";

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

const crearEstructuraVaciaAgua = () =>
  Array.from({ length: 5 }, (_, i) => ({
    semana: i + 1,
    dias: DIAS_SEMANA.reduce((acc, dia) => {
      acc[dia] = { sabor: '', olor: '', color: '', materia: '', cloroDato: '', cloro: '', phDato: '', ph: '' };
      return acc;
    }, {})
  }));

const combinarSemanasAgua = (registrosMes = []) => {
  const base = crearEstructuraVaciaAgua();
  let muestraDetectada = '';
  let mantenimientosDetectados = [];

  [...registrosMes].reverse().forEach((reg) => {
    let resp = reg?.respuestas || {};
    if (typeof resp === 'string') {
      try { resp = JSON.parse(resp); } catch { resp = {}; }
    }

    const mTomada = String(resp.muestra_tomada_de || resp.muestraTomadaDe || '').trim();
    if (mTomada) muestraDetectada = mTomada;

    if (Array.isArray(resp.mantenimientos) && resp.mantenimientos.length > 0) {
      mantenimientosDetectados = resp.mantenimientos;
    }

    const listaSemanas = resp.mediciones || resp.semanas || (Array.isArray(resp) ? resp : []);
    if (!Array.isArray(listaSemanas)) return;

    listaSemanas.forEach((semItem, idx) => {
      const numSem = Number(semItem?.semana || idx + 1);
      const targetSem = base[numSem - 1];
      if (!targetSem || !semItem?.dias) return;

      DIAS_SEMANA.forEach((dia) => {
        const datosDia = semItem.dias[dia] || (dia === 'Miércoles' ? semItem.dias['Miercoles'] : null);
        if (!datosDia) return;
        Object.entries(datosDia).forEach(([campo, valor]) => {
          if (valor !== undefined && valor !== null && String(valor).trim() !== '') {
            targetSem.dias[dia][campo] = String(valor).trim();
          }
        });
      });
    });
  });

  return {
    mediciones: base,
    muestra_tomada_de: muestraDetectada,
    mantenimientos: mantenimientosDetectados
  };
};

export const useFormularioDinamico = (slug, usuario) => {
  const { request } = useApi();
  const [formulario, setFormulario] = useState(null);
  const [respuestas, setRespuestas] = useState({});
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  
  const [sedesDisponibles, setSedesDisponibles] = useState([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);

  const permisosABAC = useMemo(() => {
    try {
      const parsed = typeof usuario?.permisos_avanzados === 'string'
        ? JSON.parse(usuario.permisos_avanzados)
        : (usuario?.permisos_avanzados || {});
      return parsed.micros || parsed;
    } catch (e) { return {}; }
  }, [usuario]);

  useEffect(() => {
    const inicializar = async () => {
      setLoading(true);
      try {
        const resForm = await request(`/formularios-dinamicos/${slug}`);
        if (resForm?.success && resForm.data) {
          let datosForm = Array.isArray(resForm.data) ? resForm.data[0] : resForm.data;
          if (datosForm.data) datosForm = Array.isArray(datosForm.data) ? datosForm.data[0] : datosForm.data;
          setFormulario(datosForm);
        }

        const baseUrl = process.env.REACT_APP_API_URL;
        const resSedes = await fetch(`${baseUrl}/api/sedes`);
        const dataSedes = await resSedes.json();
        const listaSedes = Array.isArray(dataSedes) ? dataSedes : (dataSedes.sedes || dataSedes.data || []);

        if (listaSedes.length > 0) {
          const cargoUpper = String(usuario?.cargo || '').toUpperCase();
          const esAdminGlobal = cargoUpper.includes('SUPER');
          const configPermiso = permisosABAC['Otros Formatos SST'] || {};
          const alcance = configPermiso.alcance || 'PROPIA';
          const sedesEspecificas = configPermiso.sedes_ids || [];

          let filtradas = listaSedes;
          if (!esAdminGlobal && alcance !== 'GLOBAL') {
            if (alcance === 'ESPECIFICA' && sedesEspecificas.length > 0) {
              filtradas = listaSedes.filter(s =>
                sedesEspecificas.includes(String(s.id)) || sedesEspecificas.includes(String(s.codigo_bodega))
              );
            } else {
              const FiltradasPorCodigo = listaSedes.filter(s =>
                String(s.codigo_bodega) === String(usuario?.codigo_bodega || usuario?.sede_id) ||
                String(s.nombre || '').trim().toUpperCase() === String(usuario?.centroTrabajo || usuario?.sede || '').trim().toUpperCase()
              );
              if (FiltradasPorCodigo.length > 0) filtradas = FiltradasPorCodigo;
            }
          }

          setSedesDisponibles(filtradas);
          
          let sedeInicial = '';
          if (filtradas.length === 1) {
            sedeInicial = String(filtradas[0].nombre || '').toUpperCase();
          } else if (usuario?.centroTrabajo) {
            const coincide = filtradas.find(s => String(s.nombre || '').trim().toUpperCase() === String(usuario.centroTrabajo).trim().toUpperCase());
            if (coincide) sedeInicial = coincide.nombre.toUpperCase();
          }

          setRespuestas(prev => ({
            ...prev,
            pdv: sedeInicial || prev.pdv || '',
            fechaEvento: new Date().toISOString().split('T')[0],
            inspector: `${usuario?.nombres || ''} ${usuario?.apellidos || ''}`.trim() || usuario?.nombre_completo || 'Inspector SST',
            matriz_semanas: slug === 'agua-potable' ? crearEstructuraVaciaAgua() : undefined,
            tabla_mantenimientos: slug === 'agua-potable' ? [] : undefined
          }));
        }
      } catch (error) {
        console.error("Error inicializando motor:", error);
      }
      setLoading(false);
    };

    if (slug) inicializar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const cargarAvanceMensualAgua = useCallback(async (pdvActual, mesFiltro) => {
    if (!pdvActual || slug !== 'agua-potable') return;
    setCargandoHistorial(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('token');

      const res = await fetch(
        `${baseUrl}/api/sst/formularios/historial/AGUA_POTABLE_SST?sede=${encodeURIComponent(pdvActual)}&mes=${encodeURIComponent(mesFiltro)}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      );

      if (res.ok) {
        const data = await res.json();
        const todos = data.data || data.registros || [];

        const registrosDeSedeMes = todos.filter((r) => {
          const sedeReg = String(r.pdv_centro || r.sede || '').trim().toUpperCase();
          const fechaReg = String(r.fecha || '').slice(0, 7);
          return sedeReg === String(pdvActual).trim().toUpperCase() && fechaReg === mesFiltro;
        });

        if (registrosDeSedeMes.length > 0) {
          const consolidado = combinarSemanasAgua(registrosDeSedeMes);
          setRespuestas(prev => ({
            ...prev,
            matriz_semanas: consolidado.mediciones,
            tabla_mantenimientos: consolidado.mantenimientos,
            muestra_tomada_de: consolidado.muestra_tomada_de || prev.muestra_tomada_de
          }));
        } else {
          setRespuestas(prev => ({
            ...prev,
            matriz_semanas: crearEstructuraVaciaAgua(),
            tabla_mantenimientos: [],
            muestra_tomada_de: ''
          }));
        }
      }
    } catch (err) {
      console.error('Error cargando historial de agua:', err);
    } finally {
      setCargandoHistorial(false);
    }
  }, [slug]);

  const handleChange = (nombre, valor) => {
    setRespuestas(prev => ({ ...prev, [nombre]: valor }));
  };

  const handleSubmit = async (e, onExito) => {
    e.preventDefault();
    setEnviando(true);
    
    const payload = slug === 'agua-potable' ? {
      tipo_formulario: 'AGUA_POTABLE_SST',
      datosGenerales: {
        pdv: respuestas.pdv,
        fecha: respuestas.fechaEvento,
        evaluador: respuestas.inspector
      },
      respuestas: {
        muestra_tomada_de: respuestas.muestra_tomada_de || 'Acueducto / Tanque',
        muestraTomadaDe: respuestas.muestra_tomada_de || 'Acueducto / Tanque',
        mediciones: respuestas.matriz_semanas,
        semanas: respuestas.matriz_semanas,
        mantenimientos: respuestas.tabla_mantenimientos
      },
      observaciones: {},
      puntaje_global: 100,
      evidencias: {}
    } : {
      usuario_documento: usuario?.documento || "SIN_DOC",
      datos_json: respuestas,
      observaciones_generales: respuestas.observaciones || ""
    };

    const endpoint = slug === 'agua-potable' ? '/api/sst/guardar' : `/formularios-dinamicos/${slug}/respuestas`;
    const metodo = 'POST';

    const baseUrl = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${baseUrl}${endpoint}`, {
        method: metodo,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(payload)
      });
      const dataRes = await res.json();
      setEnviando(false);
      if (res.ok && (dataRes.success !== false)) {
        if (onExito) onExito(dataRes);
      } else {
        alert(dataRes.message || "Error al guardar el formulario.");
      }
    } catch (err) {
      setEnviando(false);
      alert("Error de conexión al guardar.");
    }
  };

  return {
    formulario, respuestas, loading, enviando, sedes: sedesDisponibles,
    cargandoHistorial, cargarAvanceMensualAgua, handleChange, handleSubmit
  };
};